import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronDown, FaSearch } from "react-icons/fa";
import { usePortalPosition } from "../TaskManagement/usePortalPosition";
import {
  FILTER_CHEVRON_CLASS,
  FILTER_DROPDOWN_LI_RESET,
  FILTER_DROPDOWN_LIST_STYLE,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_EMBEDDED_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  cmsFilterOptionLabel,
  cmsFilterOptionValue,
  getDropdownOptionStyle,
} from "./filterDropdownConstants";

/**
 * Multi-select filter dropdown (commissions-style): searchable portaled menu, toggle to deselect.
 */
export default function CmsFilterMultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "All",
  searchPlaceholder = "Search...",
  disabled = false,
  embedded = false,
  usePortal = true,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open && usePortal, {
    placement: "bottom",
    gap: 6,
  });

  const selectedList = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const selectedKeys = useMemo(
    () => new Set(selectedList.map((item) => cmsFilterOptionValue(item))),
    [selectedList],
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      cmsFilterOptionLabel(opt).toLowerCase().includes(q),
    );
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setSearch("");
  }, [open]);

  const toggleOption = (option) => {
    const key = cmsFilterOptionValue(option);
    if (selectedKeys.has(key)) {
      onChange(selectedList.filter((item) => cmsFilterOptionValue(item) !== key));
    } else {
      onChange([...selectedList, option]);
    }
  };

  const displayLabel = useMemo(() => {
    if (selectedList.length === 0) return placeholder;
    if (selectedList.length === 1) return cmsFilterOptionLabel(selectedList[0]);
    return `${selectedList.length} selected`;
  }, [selectedList, placeholder]);

  const menu = (
    <div
      ref={menuRef}
      className={`tailwind-container overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ${
        usePortal ? "z-[10050]" : "absolute z-[60] mt-1 w-full"
      }`}
      style={usePortal ? portalStyle : undefined}
    >
      <div className="border-b border-gray-100 bg-[#FAFBFC] p-2">
        <div className="relative">
          <FaSearch
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            size={11}
          />
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-[34px] w-full rounded-lg border border-gray-200 bg-white pl-8 pr-2 text-[13px] text-gray-900 outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      <ul
        className="max-h-52 overflow-x-hidden overflow-y-auto py-1"
        style={FILTER_DROPDOWN_LIST_STYLE}
      >
        {filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-center text-[13px] text-gray-400" style={FILTER_DROPDOWN_LI_RESET}>
            No results found
          </li>
        ) : (
          filteredOptions.map((option) => {
            const key = cmsFilterOptionValue(option);
            const selected = selectedKeys.has(key);
            return (
              <li key={key} style={FILTER_DROPDOWN_LI_RESET}>
                <button
                  type="button"
                  onClick={() => toggleOption(option)}
                  className="flex w-full min-w-0 items-center justify-between gap-2 overflow-hidden text-[13px] transition"
                  style={getDropdownOptionStyle(selected)}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "#F9FAFB";
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span className="min-w-0 truncate text-left">{cmsFilterOptionLabel(option)}</span>
                  {selected && <FaCheck size={10} className="shrink-0 text-[#007BFF]" />}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );

  const triggerBase = embedded ? FILTER_TRIGGER_EMBEDDED_CLASS : FILTER_TRIGGER_CLASS;

  return (
    <div
      className={embedded ? "relative flex h-full min-w-0 w-full flex-1" : "min-w-0 w-full"}
      ref={containerRef}
    >
      {label && !embedded && <label className={FILTER_LABEL_CLASS}>{label}</label>}
      <div className={embedded ? "relative h-full min-w-0 w-full flex-1" : "relative"}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((p) => !p)}
          className={`${triggerBase} ${
            !embedded && open ? FILTER_TRIGGER_OPEN_CLASS : ""
          } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        >
          <span
            className={`min-w-0 truncate ${
              selectedList.length ? "text-slate-800" : "text-slate-400"
            }`}
          >
            {displayLabel}
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && !disabled && (usePortal ? createPortal(menu, document.body) : menu)}
      </div>
    </div>
  );
}
